<script setup>
import { nextTick, ref, watch } from "vue";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { Plus, Menu, Delete } from "@element-plus/icons-vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { chatWithKnowledge } from "@/api/aiChat.js";

const props = defineProps({
  username: {
    type: String,
    default: ""
  }
});

//  单个换行也渲染为 <br>,更贴近聊天场景
marked.setOptions({
  gfm: true,
  breaks: true
});

function renderMarkdown(content) {
  return DOMPurify.sanitize(marked.parse(content || ""));
}

//  消息结构: { role: "user" | "assistant", content: string, isError?: boolean }
const messages = ref([]);
const inputText = ref("");
const isSending = ref(false);
const messageListRef = ref(null);

// ==================== 历史会话（localStorage 本地存储） ====================
const STORAGE_KEY_PREFIX = "knowledge_chat_sessions_";
const MAX_SESSIONS = 50; // 会话数上限,超出淘汰 updatedAt 最旧的
const REMIND_AFTER_MESSAGES = 20; // 当前会话消息数达到该值时提醒用户新开对话

//  会话结构: { id, title, createdAt, updatedAt, remindShown, messages: [{role, content, isError?}] }
const sessions = ref([]);
const activeSessionId = ref(null);
const historyVisible = ref(false);

function getStorageKey() {
  return STORAGE_KEY_PREFIX + (props.username || "anonymous");
}

function loadSessions() {
  try {
    const raw = localStorage.getItem(getStorageKey());
    const parsed = raw ? JSON.parse(raw) : [];
    //  解析失败或结构异常时清空重来,不阻塞聊天
    sessions.value = Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.error(e);
    sessions.value = [];
  }
}

function saveSessions() {
  try {
    if (sessions.value.length > MAX_SESSIONS) {
      sessions.value = sessions.value
        .slice() // 避免原地排序影响响应式引用
        .sort((a, b) => b.updatedAt - a.updatedAt)
        .slice(0, MAX_SESSIONS);
    }
    localStorage.setItem(getStorageKey(), JSON.stringify(sessions.value));
  } catch (e) {
    //  隐私模式/超容量时静默降级,聊天照常
    console.warn("[KnowledgeChat] 保存历史对话失败", e);
  }
}

function genSessionId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

function cloneMessages(list) {
  return list.map((m) => ({ role: m.role, content: m.content, isError: m.isError, time: m.time }));
}

//  将当前对话归档到会话列表(幂等): 无内容跳过,有 id 则更新,否则新建
function upsertCurrentSession() {
  if (!messages.value.length) return;
  const title = (messages.value.find((m) => m.role === "user")?.content || "新对话").slice(0, 20);

  if (activeSessionId.value) {
    const current = sessions.value.find((s) => s.id === activeSessionId.value);
    if (current) {
      //  消息只增不改,长度未变说明自上次归档后没有新消息(发送完成后已实时归档),
      //  此时直接跳过,避免仅打开历史会话查看也会触发置顶导致列表顺序变化
      if (current.messages.length === messages.value.length) return;
      current.title = title;
      current.messages = cloneMessages(messages.value);
      current.updatedAt = Date.now();
      //  置顶到列表最前
      sessions.value = [current, ...sessions.value.filter((s) => s.id !== current.id)];
      saveSessions();
      return;
    }
  }

  const session = {
    id: activeSessionId.value || genSessionId(), //  发送前已预生成则复用,避免请求头与归档 id 不一致
    title,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    remindShown: false,
    messages: cloneMessages(messages.value)
  };
  activeSessionId.value = session.id;
  sessions.value.unshift(session);
  saveSessions();
}

//  当前会话消息数达到阈值时提醒一次新开对话
function remindIfThresholdReached() {
  if (!activeSessionId.value || messages.value.length < REMIND_AFTER_MESSAGES) return;
  const current = sessions.value.find((s) => s.id === activeSessionId.value);
  if (current && !current.remindShown) {
    current.remindShown = true;
    saveSessions();
    ElMessage.info("当前对话内容较多，建议点击「新对话」开启新会话，以获得更好的响应效果");
  }
}

function handleNewChat() {
  if (isSending.value) return;
  upsertCurrentSession(); // 当前已有内容则先归档
  messages.value = [];
  activeSessionId.value = null;
  historyVisible.value = false;
}

function handleOpenSession(session) {
  if (isSending.value) return;
  upsertCurrentSession(); // 先归档当前对话
  activeSessionId.value = session.id;
  messages.value = cloneMessages(session.messages);
  historyVisible.value = false;
  scrollToBottom();
}

function handleDeleteSession(id) {
  if (isSending.value) return;
  sessions.value = sessions.value.filter((s) => s.id !== id);
  if (activeSessionId.value === id) {
    activeSessionId.value = null;
    messages.value = [];
  }
  saveSessions();
}

//  一键清空全部历史会话,二次确认防止误触
function handleClearSessions() {
  if (isSending.value) return;
  ElMessageBox.confirm("确定要清空全部历史会话吗？清空后不可恢复。", "清空历史对话", {
    confirmButtonText: "清空",
    cancelButtonText: "取消",
    type: "warning"
  })
    .then(() => {
      sessions.value = [];
      activeSessionId.value = null;
      messages.value = [];
      saveSessions();
      ElMessage.success("历史会话已清空");
    })
    .catch(() => {});
}

//  今天的会话只显示时分,更早的显示完整年月日时分秒
function formatSessionTime(ts) {
  const d = new Date(ts);
  const pad = (n) => String(n).padStart(2, "0");
  const now = new Date();
  const isToday =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (isToday) {
    return `今天 ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

//  消息时间精确到秒
function formatMessageTime(ts) {
  const d = new Date(ts);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

//  切换用户时重置当前对话并加载对应用户的历史(各次发送已实时归档,无数据丢失)
watch(
  () => props.username,
  () => {
    messages.value = [];
    activeSessionId.value = null;
    loadSessions();
  }
);

loadSessions();

async function scrollToBottom() {
  await nextTick();
  const el = messageListRef.value;
  if (el) {
    el.scrollTop = el.scrollHeight;
  }
}

async function handleSend() {
  const content = inputText.value.trim();
  if (!content || isSending.value) return;

  messages.value.push({ role: "user", content, time: Date.now() });
  inputText.value = "";
  isSending.value = true;
  //  新对话首轮请求前先固定会话 id,后续归档复用同一 id,保证前后端会话一致
  if (!activeSessionId.value) {
    activeSessionId.value = genSessionId();
  }
  scrollToBottom();

  try {
    const res = await chatWithKnowledge(
      messages.value.filter((m) => !m.isError).map(({ role, content }) => ({ role, content })),
      activeSessionId.value
    );
    const reply = res?.data?.choices?.[0]?.message?.content ?? "";
    messages.value.push({
      role: "assistant",
      content: reply || "（AI 未返回内容，请稍后重试）",
      time: Date.now()
    });
  } catch (e) {
    console.error(e);
    //  request 拦截器已弹出错误通知,这里仅在气泡内补充占位,不丢失上下文
    messages.value.push({
      role: "assistant",
      content: "请求失败，请稍后重试。",
      isError: true,
      time: Date.now()
    });
  } finally {
    isSending.value = false;
    scrollToBottom();
    //  每次发送完成实时归档,防止刷新/关闭丢失
    upsertCurrentSession();
    remindIfThresholdReached();
  }
}

function handleKeydown(e) {
  if (e.key === "Enter" && !e.shiftKey) {
    e.preventDefault();
    handleSend();
  }
}
</script>

<template>
  <div class="knowledge-chat">
    <!-- 顶部工具栏: 历史对话 / 新对话 -->
    <div class="chat-toolbar">
      <button
        class="toolbar-btn"
        title="历史对话"
        @click="historyVisible = true"
        :disabled="isSending"
      >
        <el-icon><Menu /></el-icon>
        <span>历史对话</span>
      </button>
      <button class="toolbar-btn" title="新建对话" :disabled="isSending" @click="handleNewChat">
        <el-icon><Plus /></el-icon>
        <span>新对话</span>
      </button>
    </div>

    <div ref="messageListRef" class="chat-message-list">
      <div v-if="!messages.length" class="chat-empty">
        <span>你好，我是知识库助手，请输入你想咨询的问题～</span>
      </div>

      <template v-for="(msg, idx) in messages" :key="idx">
        <div class="chat-row" :class="msg.role">
          <div v-if="msg.role === 'assistant'" class="chat-avatar assistant-avatar">AI</div>
          <div class="chat-body">
            <div class="chat-meta">
              <span class="chat-name">{{
                msg.role === "assistant" ? "AI助手" : username || "用户"
              }}</span>
              <span v-if="msg.time" class="chat-time">{{ formatMessageTime(msg.time) }}</span>
            </div>
            <div class="chat-bubble" :class="{ 'is-error': msg.isError }">
              <!-- AI 回复走 markdown 渲染(已做 XSS 清洗) -->
              <div
                v-if="msg.role === 'assistant'"
                class="md-body"
                v-html="renderMarkdown(msg.content)"
              ></div>
              <template v-else>{{ msg.content }}</template>
            </div>
          </div>
          <!-- <div v-if="msg.role === 'user'" class="chat-avatar user-avatar">
            {{ (username || "-").slice(0, 1) }}
          </div> -->
        </div>
      </template>

      <div v-if="isSending" class="chat-row assistant">
        <div class="chat-avatar assistant-avatar">AI</div>
        <div class="chat-bubble typing-bubble">
          <span class="typing-dot"></span>
          <span class="typing-dot"></span>
          <span class="typing-dot"></span>
        </div>
      </div>
    </div>

    <div class="chat-input-area">
      <div class="input-box">
        <el-input
          v-model="inputText"
          type="textarea"
          :autosize="{ minRows: 1, maxRows: 5 }"
          resize="none"
          placeholder="请输入消息..."
          @keydown="handleKeydown"
        />
        <div class="send-btn-container">
          <el-button
            class="send-btn"
            type="primary"
            size="small"
            :class="{ 'is-active': inputText.trim() }"
            :loading="isSending"
            @click="handleSend"
          >
            发送
          </el-button>
        </div>
      </div>
    </div>

    <!-- 历史对话抽屉 -->
    <el-drawer
      v-model="historyVisible"
      direction="ltr"
      size="280px"
      :append-to-body="false"
      :destroy-on-close="false"
      class="knowledge-chat-history-drawer"
    >
      <template #header>
        <span class="history-title">历史对话</span>
      </template>

      <div class="history-list">
        <div v-if="!sessions.length" class="history-empty">暂无历史对话</div>
        <div
          v-for="s in sessions"
          :key="s.id"
          class="history-item"
          :class="{ active: s.id === activeSessionId }"
          @click="handleOpenSession(s)"
        >
          <div class="history-item-main">
            <div class="history-item-title" :title="s.title">{{ s.title }}</div>
            <div class="history-item-time">{{ formatSessionTime(s.createdAt) }}</div>
          </div>
          <el-icon class="history-item-delete" title="删除" @click.stop="handleDeleteSession(s.id)">
            <Delete />
          </el-icon>
        </div>
      </div>

      <template #footer>
        <div class="history-footer">
          <el-button
            type="danger"
            size="small"
            :icon="Delete"
            :disabled="!sessions.length || isSending"
            @click="handleClearSessions"
          >
            清空
          </el-button>
          <el-button
            type="primary"
            size="small"
            :icon="Plus"
            :disabled="isSending"
            @click="handleNewChat"
          >
            新对话
          </el-button>
        </div>
      </template>
    </el-drawer>
  </div>
</template>

<style lang="scss" scoped>
.knowledge-chat {
  position: relative;
  //  el-drawer 遮罩/面板是 position:fixed(相对视口),给祖先一个 transform
  //  使其成为 fixed 的包含块,抽屉才会锚定在聊天区左边缘而非浏览器左边
  transform: translate(0);
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #f5f6fa;
}

.chat-toolbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px 8px 0;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
}

.toolbar-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  color: #475569;
  font-size: 13px;
  line-height: 1.4;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    color: #409eff;
    border-color: #409eff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.chat-message-list {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.chat-empty {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 14px;
}

.chat-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;

  &.user {
    justify-content: flex-end;
  }
}

.chat-avatar {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: #fff;

  &.assistant-avatar {
    background: linear-gradient(135deg, #6a5cff 0%, #9b5cff 50%, #ff5caa 100%);
  }

  &.user-avatar {
    background: #409eff;
  }
}

.chat-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  max-width: 72%;

  .chat-row.user & {
    align-items: flex-end;
  }
}

.chat-meta {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 12px;
  line-height: 1;
  user-select: none;
}

.chat-name {
  color: #64748b;
  font-weight: 600;
}

.chat-time {
  color: #94a3b8;
}

//  用户侧(右侧)显示为 时间+名称
.chat-row.user .chat-meta {
  .chat-time {
    order: -1;
  }
}

.chat-bubble {
  max-width: 100%;
  padding: 10px 14px;
  border-radius: 10px;
  font-size: 14px;
  line-height: 1.6;
  word-break: break-word;

  .chat-row.assistant & {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-top-left-radius: 2px;
    color: #1e293b;
  }

  .chat-row.user & {
    background: #409eff;
    border-top-right-radius: 2px;
    color: #fff;
    white-space: pre-wrap;
  }

  &.is-error {
    color: #ef4444;
  }
}

.typing-bubble {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 42px;

  .typing-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #94a3b8;
    animation: typing-blink 1.2s infinite;

    &:nth-child(2) {
      animation-delay: 0.2s;
    }

    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
}

@keyframes typing-blink {
  0%,
  60%,
  100% {
    opacity: 0.3;
    transform: translateY(0);
  }
  30% {
    opacity: 1;
    transform: translateY(-2px);
  }
}

//  el-drawer 未 append-to-body,DOM 留在组件内,可用 :deep 覆盖默认样式
:deep(.el-drawer__body) {
  padding: 0;
}
:deep(.el-drawer__footer) {
  padding: 16px 16px 16px 0;
}

:deep(.el-drawer__header) {
  padding: 12px 16px 0 0;
  margin-bottom: 10px;
}

.history-title {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
}

//  底部操作区: 两个按钮平分宽度
.history-footer {
  display: flex;
  gap: 8px;

  .el-button {
    flex: 1;
  }
}

:deep(.el-drawer__footer) {
  border-top: 1px solid #e5e7eb;
}

.history-list {
  height: 100%;
  overflow-y: auto;
  padding: 8px 16px 8px 0;
}

.history-empty {
  padding: 24px 0;
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
}

.history-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  margin-bottom: 8px; // 记录之间留出间隔
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    background: #f1f5f9;
    border-color: #94a3b8;

    .history-item-delete {
      opacity: 1;
    }
  }

  &.active {
    background: #ecf5ff;
    border-color: #409eff;

    .history-item-title {
      color: #409eff;
    }
  }
}

.history-item-main {
  flex: 1;
  min-width: 0;
}

.history-item-title {
  font-size: 13px;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.history-item-time {
  margin-top: 2px;
  font-size: 12px;
  color: #94a3b8;
}

.history-item-delete {
  flex-shrink: 0;
  opacity: 0;
  color: #94a3b8;
  transition: all 0.2s;

  &:hover {
    color: #ef4444;
  }
}

.chat-input-area {
  flex-shrink: 0;
  padding: 12px 16px;
  background: #f5f6fa;
  .input-box {
    position: relative;
    border: 1px solid #e2e8f0;
    border-radius: 18px;
    background: #fff;
    transition: border-color 0.2s;
    overflow: hidden;
    padding-bottom: 32px;

    &:focus-within {
      border-color: #409eff;
    }

    :deep(.el-textarea__inner) {
      border: none;
      background: transparent;
      padding: 10px 14px;
      box-shadow: none;
      font-size: 14px;
      //  autosize 会写行内 min-height/height,需 !important 覆盖,默认精确到 60px
      min-height: 60px !important;
    }
    .send-btn-container {
      width: 100%;
      position: absolute;
      right: 10px;
      bottom: 10px;
      display: flex;
      align-items: center;
      justify-content: flex-end;
    }
  }
}
</style>

<style lang="scss">
/*  v-html 渲染的 markdown 内容,需要非 scoped 才能命中 */
.md-body {
  p {
    margin: 0 0 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul,
  ol {
    margin: 4px 0;
    padding-left: 20px;
  }

  code {
    padding: 1px 5px;
    border-radius: 4px;
    background: #f1f5f9;
    font-size: 13px;
  }

  pre {
    margin: 6px 0;
    padding: 10px;
    border-radius: 8px;
    background: #0f172a;
    overflow-x: auto;

    code {
      padding: 0;
      background: transparent;
      color: #e2e8f0;
    }
  }

  table {
    border-collapse: collapse;
    margin: 6px 0;

    th,
    td {
      border: 1px solid #e2e8f0;
      padding: 4px 10px;
    }
  }

  blockquote {
    margin: 6px 0;
    padding: 2px 10px;
    border-left: 3px solid #cbd5e1;
    color: #64748b;
  }
}
</style>
